/*
 * A function that emits a side effect and does not return anything.
 */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type Procedure = (...args: any[]) => void;

type Options = {
  shouldCallImmediate: boolean;
};

const debounce = function<F extends Procedure>(
  func: F,
  waitMilliseconds = 50,
  options: Options = {
    shouldCallImmediate: false,
  },
) {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let cancelled: boolean = false;

  // Function to clear existing timeout
  function clearExistingTimeout() {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }

  // Function to cancel next exec
  function cancel() {
    clearExistingTimeout();
    cancelled = true;
  }

  function wrapper(this: ThisParameterType<F>, ...args: Parameters<F>) {
    const context = this;

    if (cancelled) {
      return;
    }

    const doLater = function() {
      timeoutId = undefined;
      if (!options.shouldCallImmediate) {
        func.apply(context, args);
      }
    };

    const shouldCallNow =
      options.shouldCallImmediate && timeoutId === undefined;

    /* 
     * This is the basic debounce behaviour where you can call debounce 
     * function several times, but it will only execute once 
     * [before or after imposing a delay]. 
     * Each time the returned function is called, the timer starts over.
     */

    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(doLater, waitMilliseconds);

    if (shouldCallNow) {
      func.apply(context, args);
    }
  }

  wrapper.cancel = cancel;

  return wrapper;
};

export { debounce };
